// Admin Fêtes 2027 — Pricing items (Tarifs)
// CRUD for /api/admin/presence/pricing — the catalog used by the
// final-registration flow (Inscription) to compose paid baskets.
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const CATEGORY_LABELS = {
  pass:       { fr: "Pass",         en: "Pass",       color: "var(--cinnabar)" },
  repas:      { fr: "Repas",        en: "Meal",       color: "var(--gold)" },
  atelier:    { fr: "Atelier",      en: "Workshop",   color: "var(--moss)" },
  conference: { fr: "Conférence",   en: "Conference", color: "var(--lapis)" },
  autre:      { fr: "Autre",        en: "Other",      color: "var(--muted)" },
};
const CATEGORY_KEYS = ["pass", "repas", "atelier", "conference", "autre"];

const AUDIENCE_LABELS = {
  all:      { fr: "Tous publics",  en: "All audiences" },
  under_6:  { fr: "Moins de 6 ans", en: "Under 6" },
  "6_12":   { fr: "6 – 12 ans",     en: "6 – 12" },
  "13_17":  { fr: "13 – 17 ans",    en: "13 – 17" },
  adult:    { fr: "Adultes",        en: "Adults" },
  senior:   { fr: "65 ans et +",    en: "65+" },
};
const AUDIENCE_KEYS = ["all", "under_6", "6_12", "13_17", "adult", "senior"];

const emptyItem = () => ({
  code: "",
  label: { fr: "", en: "" },
  description: { fr: "", en: "" },
  price_eur: 0,
  audience: "all",
  category: "pass",
  schedule_id: "",
  event_id: "",
  active: true,
  order: 0,
});

export default function PresencePricingTab({ locale }) {
  const isFr = locale === "fr";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [scheduleEntries, setScheduleEntries] = useState([]);
  const [taggedEvents, setTaggedEvents] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [pricing, schedule, events] = await Promise.all([
          api.get("/admin/presence/pricing"),
          api.get("/admin/presence/schedule").catch(() => ({ data: { items: [] } })),
          api.get("/admin/events").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setItems(pricing.data.items || []);
        setScheduleEntries(schedule.data.items || []);
        const evList = Array.isArray(events.data) ? events.data : (events.data.items || []);
        setTaggedEvents(evList.filter((e) => (e.tags || []).includes("fetes_2027")));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);
  const reload = () => setRefreshKey((k) => k + 1);

  const save = async (item) => {
    const payload = { ...item, price_eur: Number(item.price_eur || 0), order: Number(item.order || 0) };
    if (!payload.schedule_id) delete payload.schedule_id;
    if (!payload.event_id) delete payload.event_id;
    if (item.id) {
      await api.patch(`/admin/presence/pricing/${item.id}`, payload);
    } else {
      await api.post("/admin/presence/pricing", payload);
    }
    setEditing(null);
    reload();
  };

  const remove = async (item) => {
    if (!window.confirm(isFr
      ? `Supprimer le tarif « ${item.label?.fr || item.code} » ?`
      : `Delete pricing "${item.label?.en || item.code}"?`)) return;
    await api.delete(`/admin/presence/pricing/${item.id}`);
    reload();
  };

  const toggleActive = async (item) => {
    await api.patch(`/admin/presence/pricing/${item.id}`, { active: !item.active });
    reload();
  };

  return (
    <div className="space-y-6" data-testid="pricing-tab">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl">{isFr ? "Tarifs des Fêtes 2027" : "Pricing — Feasts 2027"}</h2>
          <p className="font-body text-sm" style={{ color: "var(--muted)" }}>
            {isFr
              ? "Catalogue des passes, repas, ateliers et conférences proposés à l'inscription définitive (avec paiement SumUp)."
              : "Catalog of passes, meals, workshops and conferences offered at final registration (with SumUp payment)."}
          </p>
        </div>
        <button data-testid="pricing-add"
                onClick={() => setEditing(emptyItem())}
                className="btn btn-primary">
          + {isFr ? "Nouveau tarif" : "New price"}
        </button>
      </div>

      {loading ? (
        <p className="font-body" style={{ color: "var(--muted)" }}>…</p>
      ) : items.length === 0 ? (
        <div className="admin-card text-center py-12">
          <p className="font-display italic" style={{ color: "var(--muted)" }}>
            {isFr ? "Aucun tarif défini. Ajoutez un premier article (Pass, repas, atelier…) pour ouvrir les inscriptions payantes." : "No pricing yet. Add a first item to enable paid registrations."}
          </p>
        </div>
      ) : (
        <PricingTable items={items} isFr={isFr}
                      onEdit={setEditing}
                      onToggle={toggleActive}
                      onDelete={remove}
                      scheduleEntries={scheduleEntries}
                      taggedEvents={taggedEvents} />
      )}

      {editing && (
        <PricingEditor item={editing} isFr={isFr}
                       scheduleEntries={scheduleEntries}
                       taggedEvents={taggedEvents}
                       onCancel={() => setEditing(null)}
                       onSave={save} />
      )}
    </div>
  );
}

function PricingTable({ items, isFr, onEdit, onToggle, onDelete, scheduleEntries, taggedEvents }) {
  // Group by category
  const groups = CATEGORY_KEYS.map((cat) => ({
    cat,
    items: items.filter((i) => (i.category || "autre") === cat),
  })).filter((g) => g.items.length > 0);
  return (
    <div className="space-y-6" data-testid="pricing-table">
      {groups.map((g) => (
        <div key={g.cat}>
          <div className="font-caps text-[0.65rem] mb-2" style={{ color: CATEGORY_LABELS[g.cat].color, letterSpacing: "0.22em" }}>
            ◆ {isFr ? CATEGORY_LABELS[g.cat].fr : CATEGORY_LABELS[g.cat].en}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full font-body text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: "var(--border)" }}>
                  <th className="text-left p-2 label-caps">{isFr ? "Code" : "Code"}</th>
                  <th className="text-left p-2 label-caps">{isFr ? "Libellé" : "Label"}</th>
                  <th className="text-left p-2 label-caps">{isFr ? "Public" : "Audience"}</th>
                  <th className="text-left p-2 label-caps">{isFr ? "Lié à" : "Linked to"}</th>
                  <th className="text-right p-2 label-caps">{isFr ? "Prix" : "Price"}</th>
                  <th className="text-center p-2 label-caps">{isFr ? "Actif" : "Active"}</th>
                  <th className="text-right p-2 label-caps">{isFr ? "Actions" : "Actions"}</th>
                </tr>
              </thead>
              <tbody>
                {g.items.map((it) => {
                  const linked = it.schedule_id
                    ? (scheduleEntries.find((s) => s.id === it.schedule_id)?.title?.fr || "— créneau —")
                    : it.event_id
                    ? (taggedEvents.find((e) => e.id === it.event_id)?.title?.fr || "— événement —")
                    : "—";
                  return (
                    <tr key={it.id} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`pricing-row-${it.code}`}>
                      <td className="p-2 font-mono text-xs" style={{ color: "var(--ink-2)" }}>{it.code}</td>
                      <td className="p-2">
                        <div className="font-display">{(it.label || {}).fr || (it.label || {}).en}</div>
                        {(it.description || {}).fr && (
                          <div className="text-xs italic" style={{ color: "var(--muted)" }}>{(it.description).fr}</div>
                        )}
                      </td>
                      <td className="p-2 text-xs" style={{ color: "var(--ink-2)" }}>
                        {isFr ? AUDIENCE_LABELS[it.audience]?.fr : AUDIENCE_LABELS[it.audience]?.en}
                      </td>
                      <td className="p-2 text-xs" style={{ color: "var(--muted)" }}>{linked}</td>
                      <td className="p-2 text-right font-display">{Number(it.price_eur).toFixed(2)} €</td>
                      <td className="p-2 text-center">
                        <button onClick={() => onToggle(it)}
                                data-testid={`pricing-toggle-${it.code}`}
                                className="font-caps text-[0.6rem] px-2 py-1 border"
                                style={{
                                  borderColor: it.active ? "var(--moss)" : "var(--muted)",
                                  color: it.active ? "var(--moss)" : "var(--muted)",
                                  letterSpacing: "0.16em",
                                }}>
                          {it.active ? (isFr ? "OUI" : "ON") : (isFr ? "NON" : "OFF")}
                        </button>
                      </td>
                      <td className="p-2 text-right space-x-2">
                        <button onClick={() => onEdit(it)} data-testid={`pricing-edit-${it.code}`}
                                className="font-caps text-[0.6rem]" style={{ color: "var(--ink)", letterSpacing: "0.18em" }}>
                          ✎ {isFr ? "ÉDITER" : "EDIT"}
                        </button>
                        <button onClick={() => onDelete(it)} data-testid={`pricing-delete-${it.code}`}
                                className="font-caps text-[0.6rem]" style={{ color: "var(--cinnabar)", letterSpacing: "0.18em" }}>
                          ✕ {isFr ? "SUPPR" : "DEL"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function PricingEditor({ item, isFr, scheduleEntries, taggedEvents, onCancel, onSave }) {
  const [it, setIt] = useState(item);
  const set = (k, v) => setIt((p) => ({ ...p, [k]: v }));
  const setLocalized = (group, sub, v) => setIt((p) => ({ ...p, [group]: { ...p[group], [sub]: v } }));
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto"
         style={{ background: "rgba(8,6,4,0.78)" }} data-testid="pricing-editor">
      <div className="bg-white border w-full max-w-3xl p-6 sm:p-8 my-8"
           style={{ borderColor: "var(--border)" }}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-xl">
            {it.id ? (isFr ? "Modifier le tarif" : "Edit pricing") : (isFr ? "Nouveau tarif" : "New pricing")}
          </h3>
          <button onClick={onCancel} className="text-2xl" style={{ color: "var(--muted)" }}>×</button>
        </div>
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label={isFr ? "Code (interne) *" : "Code (internal) *"} testId="pricing-code"
                   value={it.code} onChange={(v) => set("code", v.toUpperCase().replace(/\s+/g, "_"))} />
            <Field label={isFr ? "Prix (€) *" : "Price (€) *"} testId="pricing-price" type="number" step="0.01"
                   value={it.price_eur} onChange={(v) => set("price_eur", v)} />
            <SelectField label={isFr ? "Catégorie" : "Category"} testId="pricing-category"
                         value={it.category} onChange={(v) => set("category", v)}
                         options={CATEGORY_KEYS.map((k) => ({ v: k, l: isFr ? CATEGORY_LABELS[k].fr : CATEGORY_LABELS[k].en }))} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={isFr ? "Libellé FR *" : "Label FR *"} testId="pricing-label-fr"
                   value={(it.label || {}).fr || ""} onChange={(v) => setLocalized("label", "fr", v)} />
            <Field label={isFr ? "Libellé EN" : "Label EN"} testId="pricing-label-en"
                   value={(it.label || {}).en || ""} onChange={(v) => setLocalized("label", "en", v)} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextArea label={isFr ? "Description FR" : "Description FR"} testId="pricing-desc-fr"
                      value={(it.description || {}).fr || ""} onChange={(v) => setLocalized("description", "fr", v)} />
            <TextArea label={isFr ? "Description EN" : "Description EN"} testId="pricing-desc-en"
                      value={(it.description || {}).en || ""} onChange={(v) => setLocalized("description", "en", v)} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <SelectField label={isFr ? "Public visé" : "Audience"} testId="pricing-audience"
                         value={it.audience} onChange={(v) => set("audience", v)}
                         options={AUDIENCE_KEYS.map((k) => ({ v: k, l: isFr ? AUDIENCE_LABELS[k].fr : AUDIENCE_LABELS[k].en }))} />
            <Field label={isFr ? "Ordre d'affichage" : "Display order"} testId="pricing-order" type="number"
                   value={it.order || 0} onChange={(v) => set("order", v)} />
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={!!it.active}
                     onChange={(e) => set("active", e.target.checked)}
                     data-testid="pricing-active" />
              <span className="font-caps text-[0.7rem]" style={{ letterSpacing: "0.2em" }}>
                {isFr ? "Tarif actif" : "Active"}
              </span>
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SelectField label={isFr ? "Lier à un créneau (optionnel)" : "Link to schedule entry (optional)"} testId="pricing-schedule"
                         value={it.schedule_id || ""} onChange={(v) => set("schedule_id", v)}
                         options={[
                           { v: "", l: isFr ? "— Aucun —" : "— None —" },
                           ...scheduleEntries.map((s) => ({
                             v: s.id,
                             l: `${s.day || ""} ${s.start_time || ""} · ${(s.title || {}).fr || (s.title || {}).en || s.id}`,
                           })),
                         ]} />
            <SelectField label={isFr ? "Lier à un événement Fêtes 2027 (optionnel)" : "Link to a Fêtes 2027 event (optional)"} testId="pricing-event"
                         value={it.event_id || ""} onChange={(v) => set("event_id", v)}
                         options={[
                           { v: "", l: isFr ? "— Aucun —" : "— None —" },
                           ...taggedEvents.map((e) => ({
                             v: e.id,
                             l: `${e.date || ""} · ${(e.title || {}).fr || (e.title || {}).en || e.id}`,
                           })),
                         ]} />
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-8">
          <button onClick={onCancel} className="btn btn-outline" data-testid="pricing-cancel">
            {isFr ? "Annuler" : "Cancel"}
          </button>
          <button onClick={() => onSave(it)} className="btn btn-primary" data-testid="pricing-save">
            {isFr ? "Enregistrer" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testId, value, onChange, type = "text", step }) {
  return (
    <label className="block">
      <span className="label-caps">{label}</span>
      <input data-testid={testId} type={type} step={step}
             value={value ?? ""} onChange={(e) => onChange(e.target.value)}
             className="input-classic mt-1 w-full" />
    </label>
  );
}

function TextArea({ label, testId, value, onChange }) {
  return (
    <label className="block">
      <span className="label-caps">{label}</span>
      <textarea data-testid={testId} rows={2}
                value={value ?? ""} onChange={(e) => onChange(e.target.value)}
                className="input-classic mt-1 w-full" />
    </label>
  );
}

function SelectField({ label, testId, value, onChange, options }) {
  return (
    <label className="block">
      <span className="label-caps">{label}</span>
      <select data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
              className="input-classic mt-1 w-full" style={{ background: "transparent" }}>
        {options.map((o) => (
          <option key={o.v} value={o.v}>{o.l}</option>
        ))}
      </select>
    </label>
  );
}
